// MSSQL batch-level local-variable completion (Workstream C, mssql 41.§6-§12).
//
// `emitMssqlProceduralLocalCandidates` is gated on `isInsideProceduralBody`,
// so `DECLARE @x` statements at batch top level (the `sp_executesql` pattern:
// DECLARE → SET @sql → EXEC … @param = @local) never surfaced anywhere.
// `resolveScopeTSql` already implements T-SQL batch visibility (declaration →
// end of GO-batch, cross-GO invisible), so this module only adds the
// batch-side entry point plus a recency split: the most recently *assigned*
// local ranks tier 1 and the rest tier 2 ("SELECT ▮@RowCount AS Matched"
// right after an OUTPUT capture should offer @RowCount first).
//
// Scalar variables ONLY — `scope.tableVariables` / #temp tables are relations
// and are already emitted by `emitSyntheticRelationsAsCandidates`; emitting
// them here too would duplicate labels and fight the caret-binding promotion
// at FROM slots (script.45).
//
// Perf (plan16 T2): scope resolution runs on the T-SQL parse worker via
// `getProceduralScope` (with an inline fallback ladder). Callers still gate on
// `hasBatchLocalDeclarations` first — buffers without a `DECLARE @` before the
// caret never pay the round-trip. The recency split
// (`findMostRecentlySetLocal`) stays main-thread: it is a cheap regex over the
// caret prefix, and the worker only supplies the visible names.

import type { CompletionCandidate } from '@/core/engines';
import { getProceduralScope } from './proceduralWorkerBridge';

/** Cheap pre-gate: is there any `DECLARE @…` before the caret? Only then is
 *  the ANTLR parse in `emitMssqlBatchLocalCandidates` worth paying. */
export function hasBatchLocalDeclarations(source: string, offset: number): boolean {
  return /\bDECLARE\s+@/i.test(source.slice(0, offset));
}

/**
 * Emit the scalar `@var` declarations visible at `offset` (batch-scoped via
 * the worker's `resolveScopeTSql`). The most recently assigned visible local
 * gets tier 1, the others tier 2; when no assignment precedes the caret all
 * stay tier 1. `getProceduralScope` never throws — a not-yet-loaded bundle or
 * a parse failure yields an empty scope and this becomes a no-op.
 *
 * Scalar variables ONLY: cursors and table variables (the worker's non-
 * `variable` locals) are filtered out here — table variables / #temp tables
 * are relations already emitted by `emitSyntheticRelationsAsCandidates`.
 */
export async function emitMssqlBatchLocalCandidates(
  source: string,
  offset: number,
  out: CompletionCandidate[],
): Promise<void> {
  const scope = await getProceduralScope(source, offset);
  const variables = scope.locals.filter((l) => l.kind === 'variable');
  if (variables.length === 0) return;
  const names = new Set(variables.map((v) => v.name.toLowerCase()));
  const recent = findMostRecentlySetLocal(source, offset, names);
  for (const v of variables) {
    out.push({
      label: v.name,
      insertText: v.name,
      isSnippet: false,
      kind: 'variable',
      tier: recent === undefined || v.name.toLowerCase() === recent ? 1 : 2,
      detail: `${v.typeText} (local variable)`,
      filterText: v.name,
    });
  }
}

// `@x =` (SET / SELECT-assignment / EXEC named arg; optional compound op,
// `(?!=)` rejects `==`) and `@x OUTPUT` (EXEC output capture). `(?<!@)`
// rejects `@@ROWCOUNT`-style system functions. Comparisons (`WHERE @x = 5`)
// match too — accepted: this is a recency-of-use ranking heuristic, not an
// assignment analysis.
const ASSIGNMENT_RE = /(?<!@)(@[A-Za-z_]\w*)\s*(?:[-+*/%&|^]?=(?!=)|OUTPUT\b)/gi;

/**
 * Name (lower-cased) of the visible local with the lexically-last
 * assignment-shaped reference before `offset`, or undefined when none.
 */
export function findMostRecentlySetLocal(
  source: string,
  offset: number,
  visibleNames: ReadonlySet<string>,
): string | undefined {
  const head = source.slice(0, offset);
  let winner: string | undefined;
  for (const m of head.matchAll(ASSIGNMENT_RE)) {
    const name = m[1]!.toLowerCase();
    if (visibleNames.has(name)) winner = name;
  }
  return winner;
}
